import React, { useCallback, useContext, useState } from "react";
import { Button, ButtonGroup, Popover, InlineStack, Box } from "@shopify/polaris";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";

import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import storageHelper from "lib/helpers/storage";
import appConfig from "configuration";
import quickSettingConfig from "app/reports/operator/widgets/quickSetting/quickSettingConfig";
import { DropDown } from "app/reports/generic/style/quickAction";
import quickAction from "./quickActionConfig";

const { brandPageV2URL } = appConfig;

const OperatorQuickAction = (props) => {
  const { link = [] } = props;
  const { chatSetting = {}, cms, currentUser } = useContext(PrivateContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname: path } = location;

  const quickActionConfig = quickAction(cms);
  const { config, title } = quickSettingConfig(cms);
  const [popoverActive, setPopoverActive] = useState(false);

  const { isVendor: isChatVendor = true } = chatSetting || {};

  const currentUserContext = {
    brandName: currentUser.brandName,
    domain: brandPageV2URL,
    shop: currentUser.shop,
    token: storageHelper.get("token"),
  };

  const actionCard = (action, idx, secondary) => {
    const onAction = (action.action && action.action.bind(currentUserContext)) || (() => navigate(action.link));
    return (
      <Button
        key={action.id || action.label || idx}
        // variant="primary"
        variant={secondary ? "secondary" : "primary"}
        onClick={onAction}
        disabled={action.disabled}
        loading={action.loading}
      >
        {action.label}
      </Button>
    );
  };

  const togglePopoverActive = useCallback(() => setPopoverActive((prev) => !prev), []);

  const activator = (
    <Button onClick={togglePopoverActive} disclosure>
      {title[path] || cms("common.actions.title.setting")}
    </Button>
  );

  const getQuickActions = (actions = [], sec = false) => {
    let updated = [...actions];
    if (currentUser.ecommercePlatform || !isChatVendor) {
      updated = updated.filter((item) => item.id !== "chatInbox");
    }
    if (!updated.length) return null;
    return <ButtonGroup>{updated.map((a, i) => actionCard(a, i, sec))}</ButtonGroup>;
  };

  const getQuickSettings = (settings = []) => {
    if (!settings?.length) return null;

    // Single setting: render as a simple button to match the other groups
    if (settings.length === 1) {
      const s = settings[0];
      const onAction = s.action ? s.action.bind(currentUserContext) : () => navigate(s.link);
      return (
        <Button key={s.id || s.label} onClick={onAction}>
          {s.label}
        </Button>
      );
    }

    // Multiple settings: use popover menu
    const items = settings.map((s, i) => {
      const click = s.link ? () => navigate(s.link) : s.action?.bind(currentUserContext);
      return (
        <li key={s.id || s.label || i}>
          <Button variant="plain" onClick={click}>
            {s.label}
          </Button>
        </li>
      );
    });

    return (
      <Popover active={popoverActive} activator={activator} onClose={togglePopoverActive}>
        <DropDown className="drop-down">{items}</DropDown>
      </Popover>
    );
  };

  const quickActions = (link.length && link) || quickActionConfig[path] || [];
  const primaryActions = quickActions.filter((qa) => qa.primary);
  const secondaryActions = quickActions.filter((qa) => !qa.primary);

  const showSettings = !(currentUser.ecommercePlatform && path === constant.PROVIDER_LIST) && config[path];

  return (
    <>
      <InlineStack align="space-between" blockAlign="center" wrap={false}>
        {/* Left cluster: primary actions */}
        <div>{getQuickActions(primaryActions)}</div>

        {/* Right clusters: secondary actions + settings */}
        <InlineStack blockAlign="center" wrap={false} gap="400">
          <div>{getQuickActions(secondaryActions, true)}</div>

          {showSettings ? <Box paddingInlineStart="3">{getQuickSettings(config[path])}</Box> : null}
        </InlineStack>
      </InlineStack>
      <br />
    </>
  );
};

OperatorQuickAction.propTypes = {
  link: PropTypes.arrayOf(PropTypes.object),
};

OperatorQuickAction.defaultProps = {
  link: [],
};

export default OperatorQuickAction;
